import confetti from 'canvas-confetti';
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { Fragment, useEffect, useRef } from 'react';
import { ApiError, type ApiErrorSingle } from '~/api';
import type { ButtonProps } from '~/components/new/Button';
import IconAction from '~/components/new/IconAction';
import { isVisualRegression } from '~/utils/env';
import { parseError } from '~/utils/format';
import { classNames } from '~/utils/styles';
import Icon, { RemixIconNames, type IconNames } from './Icon';
import MoreOptions, { type MoreOptionsItemProps, type MoreOptionsProps } from './MoreOptions';

export const variants = ['info', 'danger', 'warn', 'success', 'celebration'] as const;
type AlertVariant = (typeof variants)[number];
type ActionsType = ReactElement<ButtonProps> | ReactElement<MoreOptionsProps>;

export type AlertProps = HTMLAttributes<HTMLDivElement> & {
    variant?: AlertVariant;
    actions?: ActionsType | ActionsType[];
    onClose?: () => void;
    moreOptions?: ReactElement<MoreOptionsProps>;
    icon?: IconNames;
    confetti?: boolean;
};

const VARIANT_ICONS: Record<AlertVariant, IconNames> = {
    info: RemixIconNames.RiInformationFill,
    success: RemixIconNames.RiCheckboxCircleFill,
    warn: RemixIconNames.RiAlertFill,
    danger: RemixIconNames.RiForbid2Fill,
    celebration: RemixIconNames.RiStarSmileFill,
};

const CELEBRATION_COLORS = [
    '#4040E0',
    '#002080',
    '#0080E0',
    '#2040C0',
    '#80A0E0',
    '#C0C0E0',
    '#4060E0',
];

function CelebrationConfetti() {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (isVisualRegression()) {
            return;
        }

        const canvas = canvasRef.current;
        if (!canvas) return;

        const fire = confetti.create(canvas, { resize: true, useWorker: false });

        const streamer = confetti.shapeFromPath({
            path: 'M1,0 Q8,9 1,18 Q-6,27 1,36 L3,36 Q10,27 3,18 Q-4,9 3,0 Z',
        });

        const BURST_BASE = {
            gravity: 0.3,
            scalar: 0.5,
            ticks: 220,
            origin: { x: 0.5, y: 1.3 },
            colors: CELEBRATION_COLORS,
            shapes: ['square', 'circle', streamer] as confetti.Shape[],
            flat: false,
        };

        let delayId: ReturnType<typeof setTimeout> | undefined;

        function burst() {
            fire({
                ...BURST_BASE,
                particleCount: 45,
                angle: 80,
                spread: 40,
                startVelocity: 10,
                drift: 0.5,
                origin: { x: 0.0, y: 1.3 },
            });
            delayId = setTimeout(() => {
                fire({
                    ...BURST_BASE,
                    particleCount: 35,
                    angle: 100,
                    spread: 35,
                    startVelocity: 12,
                    drift: 0.5,
                    origin: { x: 0.15, y: 1.3 },
                });
            }, 100);
        }

        burst();
        const id = setInterval(burst, 7000);
        return () => {
            clearInterval(id);
            clearTimeout(delayId);
            fire.reset();
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className='alert-celebration-animation'
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
            }}
        />
    );
}

function keyedActions(actions: ActionsType | ActionsType[]): ReactNode {
    return (Array.isArray(actions) ? actions : [actions])
        .filter(Boolean)
        .map((action, index) => (
            <Fragment key={action.key ?? `alert-action-${index}`}>{action}</Fragment>
        ));
}

export default function Alert({
    variant = 'info',
    children,
    className,
    actions = [],
    moreOptions,
    onClose,
    icon,
    confetti = true,
    ...props
}: AlertProps): ReactElement {
    return (
        <div
            data-test-component='ui/Alert'
            {...props}
            className={classNames({
                [`alert alert-${variant}`]: true,
                [`${className}`]: Boolean(className),
            })}>
            {variant === 'celebration' && confetti && <CelebrationConfetti />}
            <div className='alert-content'>
                <div className='alert-icon'>
                    <Icon icon={icon ?? VARIANT_ICONS[variant]} />
                </div>
                <div className='alert-text'>{children}</div>
                {actions && <div className='alert-actions'>{keyedActions(actions)}</div>}
                {moreOptions && <div className='alert-options'>{moreOptions}</div>}
                {onClose && (
                    <IconAction
                        onClick={onClose}
                        title='Close'
                        icon={RemixIconNames.RiCloseFill}
                    />
                )}
            </div>
            {actions && <div className='alert-mobile-actions'>{keyedActions(actions)}</div>}
        </div>
    );
}

// shortcut for More Options in Alert actions
Alert.MoreOptions = function AlertMoreOptions({
    title = 'More actions',
    options,
}: {
    title?: string;
    options?: MoreOptionsItemProps[];
}) {
    return (
        <MoreOptions
            size='small'
            title={title}>
            {options?.map((option) => (
                <MoreOptions.Item
                    key={option.label}
                    {...option}
                />
            ))}
        </MoreOptions>
    );
};

export function ErrorAlert({
    errors = [],
    formatError,
    children,
    ...alertProps
}: Omit<AlertProps, 'variant'> & ErrorsProps): ReactElement {
    return (
        <div
            className='error-alert'
            data-test-component='ui/Alert/ErrorAlert'>
            <Alert
                {...alertProps}
                variant='danger'>
                {children}
                <Errors
                    errors={errors}
                    formatError={formatError}
                />
            </Alert>
        </div>
    );
}

type ErrorsProps = {
    errors?: ApiError | null;
    formatError?: (e: string) => string;
};

export function Errors({ errors = [], formatError = (e) => e }: ErrorsProps): ReactElement | null {
    if (!errors) {
        return null;
    }

    return (
        <>
            {[errors]
                .flat()
                .map((e) => parseError(e as ApiErrorSingle).message)
                .filter(Boolean)
                .map((e, i) => (
                    <span key={`alert_error_${i}`}>{formatError(e!)}</span>
                ))}
        </>
    );
}

export function Celebration({ ...props }: AlertProps) {
    return (
        <Alert
            {...props}
            variant='celebration'
        />
    );
}
